import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { PatientMessageConversation } from '@/utils/patientMessagesPath';
import { unreadConversationCount } from '@/utils/patientMessagesPath';
import { fillMissingClinicSenderNames } from '@/utils/fillClinicSenderNames';

export interface PatientMessage {
  id: string;
  clinic_id: string;
  patient_id: string;
  sender: 'patient' | 'clinic';
  sender_user_id: string | null;
  sender_name: string | null;
  content: string;
  read_at: string | null;
  read_by: string | null;
  created_at: string;
  updated_at: string;
}

export const patientMessagesQueryKey = (patientId: string | null, clinicId?: string | null) =>
  clinicId
    ? (['messages', 'patient', patientId, clinicId] as const)
    : (['messages', 'patient', patientId] as const);

export const patientMessagesInboxQueryKey = (clinicId: string | null) =>
  ['messages', 'inbox', clinicId] as const;

function invalidateMessageViews(
  queryClient: ReturnType<typeof useQueryClient>,
  patientId?: string,
) {
  if (patientId) {
    queryClient.invalidateQueries({
      queryKey: patientMessagesQueryKey(patientId),
    });
  }
  queryClient.invalidateQueries({ queryKey: ['messages', 'inbox'] });
}

export function usePatientMessages(
  patientId: string | null | undefined,
  clinicId?: string | null,
) {
  return useQuery({
    queryKey: patientMessagesQueryKey(patientId ?? null, clinicId),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientMessage[]> => {
      if (!patientId) return [];

      const { data, error } = await supabase.rpc(
        'get_patient_messages',
        clinicId
          ? { p_patient_id: patientId, p_clinic_id: clinicId }
          : { p_patient_id: patientId },
      );

      if (error) throw error;

      const rows = ((data ?? []) as PatientMessage[]).filter(
        (row) => !clinicId || row.clinic_id === clinicId,
      );
      return fillMissingClinicSenderNames(rows);
    },
    staleTime: 15_000,
  });
}

export function usePatientMessageInbox(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: patientMessagesInboxQueryKey(clinicId ?? null),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<PatientMessageConversation[]> => {
      if (!clinicId) return [];

      const { data, error } = await supabase.rpc('list_patient_message_conversations', {
        p_clinic_id: clinicId,
      });

      if (error) throw error;

      return (data ?? []).map((row) => ({
        patient_id: row.patient_id,
        patient_first_name: row.patient_first_name,
        patient_last_name: row.patient_last_name,
        latest_message_id: row.latest_message_id,
        latest_content: row.latest_content,
        latest_sender: row.latest_sender,
        latest_sender_name: row.latest_sender_name,
        latest_created_at: row.latest_created_at,
        latest_has_attachment: row.latest_has_attachment,
        unread_count: Number(row.unread_count ?? 0),
      }));
    },
    staleTime: 15_000,
  });
}

export function useUnreadPatientMessageCount(clinicId: string | null | undefined) {
  const inbox = usePatientMessageInbox(clinicId);
  return {
    ...inbox,
    data: unreadConversationCount(inbox.data ?? []),
  };
}

export function useSendPatientMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { patient_id: string; content: string }) => {
      const { data, error } = await supabase.rpc('send_patient_message', {
        p_patient_id: params.patient_id,
        p_content: params.content,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      invalidateMessageViews(queryClient, variables.patient_id);
    },
  });
}

export function useMarkMessageRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { message_id: string; patient_id: string }) => {
      const { data, error } = await supabase.rpc('mark_message_read', {
        p_message_id: params.message_id,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      invalidateMessageViews(queryClient, variables.patient_id);
    },
  });
}
